import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { createWarehouseScanFilter } from '@/lib/warehouse-scan-filter.mjs';

// Quagga owns a singleton camera. Finish teardown before a switch/reopen can initialize it.
let cameraSession = Promise.resolve();

// Quagga2 decodes locally; no camera frames are uploaded.
export default function WarehouseCameraScanner({ onDetected, onClose, paused = false, children, supplierName, title = 'Scan penerimaan' }) {
  const viewport = useRef(null), callbacks = useRef({ onDetected, onClose, paused });
  const locked = useRef(false);
  callbacks.current = { onDetected, onClose, paused };
  useEffect(() => {
    if (!paused) {
      locked.current = false;
      viewport.current?.closest('[role="dialog"]')?.scrollTo({ top: 0 });
    }
  }, [paused]);
  const [error, setError] = useState(''), [ready, setReady] = useState(false);
  const [devices, setDevices] = useState([]), [device, setDevice] = useState('');
  useEffect(() => {
    let disposed = false, release;
    let target;
    const closed = new Promise(resolve => { release = resolve; });
    const filter = createWarehouseScanFilter();
    const stopTracks = () => target?.querySelector('video')?.srcObject?.getTracks().forEach(track => track.stop());
    setError(''); setReady(false);
    const stop = () => {
      disposed = true;
      stopTracks();
      release();
    };
    const pause = () => { if (document.hidden) { stop(); callbacks.current.onClose(); } };
    document.addEventListener('visibilitychange', pause);
    cameraSession = cameraSession.then(async () => {
      let quagga;
      const process = result => {
        if (disposed) return;
        const code = filter(result, callbacks.current.paused || locked.current);
        if (!code) return;
        locked.current = true;
        callbacks.current.onDetected(code);
      };
      try {
        if (disposed) return;
        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
          throw new Error('Buka aplikasi melalui HTTPS agar kamera HP dapat digunakan.');
        }
        const { default: Quagga } = await import('@ericblade/quagga2');
        if (disposed) return;
        // Dialog renders its portal after the parent's first effect has run.
        target = viewport.current;
        if (!target) throw new Error('Tampilan kamera belum siap. Tutup lalu buka pemindai kembali.');
        quagga = Quagga;
        await quagga.init({
          inputStream: {
            type: 'LiveStream', target,
            constraints: {
              width: { ideal: 1280 }, height: { ideal: 720 },
              ...(device ? { deviceId: { exact: device } } : { facingMode: { ideal: 'environment' } }),
            },
          },
          locate: true, frequency: 10,
          locator: { halfSample: true, patchSize: 'x-large' },
          decoder: { readers: ['ean_reader', 'ean_8_reader', 'code_128_reader', 'upc_reader', 'upc_e_reader', 'code_39_reader'], multiple: false },
        });
        if (disposed) return;
        const track = quagga.CameraAccess.getActiveTrack();
        if (track?.getCapabilities?.().focusMode?.includes('continuous')) {
          await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {});
        }
        if (disposed) return;
        locked.current = false;
        quagga.onProcessed(process);
        quagga.start();
        setReady(true);
        // Some browsers allow scanning but disallow device enumeration.
        navigator.mediaDevices.enumerateDevices?.().then(cameras => {
          if (!disposed) setDevices(cameras.filter(camera => camera.kind === 'videoinput' && camera.deviceId));
        }).catch(() => {});
        await closed;
      } catch (failure) {
        if (disposed) return;
        stop();
        setError(failure.name === 'NotAllowedError' ? 'Izin kamera ditolak. Izinkan kamera di pengaturan browser, lalu buka pemindai kembali.' : failure.name === 'NotFoundError' ? 'Kamera tidak ditemukan. Gunakan input barcode manual atau scanner.' : failure.name === 'NotReadableError' ? 'Kamera sedang dipakai aplikasi lain. Tutup aplikasi tersebut lalu coba lagi.' : failure.message || 'Kamera gagal dibuka. Coba lagi atau ketik barcode.');
      } finally {
        if (quagga) {
          quagga.offProcessed(process);
          try { await quagga.stop(); } catch { /* Also release tracks directly below. */ }
          stopTracks();
        }
      }
    });
    return () => { stop(); document.removeEventListener('visibilitychange', pause); };
  }, [device]);
  return <Dialog open onOpenChange={open => { if (!open) onClose(); }}><DialogContent onOpenAutoFocus={e => e.preventDefault()} onCloseAutoFocus={e => e.preventDefault()} onInteractOutside={e => e.preventDefault()} className="bg-ui-card w-full max-w-lg h-[100dvh] max-h-[100dvh] sm:h-auto sm:max-h-[94dvh] !p-3 sm:!p-5 overflow-y-auto !gap-3 !rounded-none sm:!rounded-xl content-start" data-testid="warehouse-camera-dialog"><DialogHeader className="text-left pr-8"><DialogTitle>{title}</DialogTitle><DialogDescription>{supplierName} · Scan → isi QTY → simpan → scan berikutnya.</DialogDescription></DialogHeader>
    <div ref={viewport} className={`warehouse-camera-viewport relative w-full overflow-hidden rounded-lg bg-black [&>canvas]:hidden ${paused ? 'h-[18dvh] max-h-36' : 'h-[35dvh] max-h-72'}`}>
      <video muted autoPlay playsInline data-testid="warehouse-camera-video" />
    </div>
    <p className="text-sm" role="status" data-testid="warehouse-camera-status">{error || (ready ? paused ? 'Pembacaan dijeda. Selesaikan barang ini; kamera tetap aktif.' : 'Siap scan berikutnya. Jauhkan barcode sebelumnya sebelum memindai barang yang sama lagi.' : 'Menyiapkan kamera…')}</p>
    {!error && !paused && <p className="text-xs text-ui-muted">Arahkan satu barcode batang hingga seluruh garis terlihat jelas. Tahan sebentar sampai terbaca.</p>}
    {children}
    {devices.length > 1 && !paused && <label className="text-sm">Pilih kamera<select aria-label="Pilih kamera" className="theme-input w-full mt-1" value={device} onChange={e => setDevice(e.target.value)}><option value="">Kamera belakang otomatis</option>{devices.map((camera, index) => <option key={camera.deviceId} value={camera.deviceId}>{camera.label || `Kamera ${index + 1}`}</option>)}</select></label>}
    <button type="button" className="theme-outline min-h-12 w-full" data-testid="warehouse-camera-close" onClick={onClose}>Selesai scan / tutup kamera</button>
  </DialogContent></Dialog>;
}
